<template>
  <v-container fluid class="activity-logs-page pa-4">

    <!-- ACCESS DENIED for non-admins -->
    <div v-if="!isAdmin" class="d-flex flex-column align-center justify-center" style="height:60vh">
      <v-icon size="80" color="red lighten-2">mdi-shield-lock-outline</v-icon>
      <h2 class="mt-4 red--text text--darken-1">Access Denied</h2>
      <p class="grey--text mt-2">Only administrators can view activity logs.</p>
    </div>

    <template v-else>
      <!-- ───── Header ───── -->
      <div class="d-flex align-center mb-5">
        <div>
          <h1 class="text-h5 font-weight-bold logs-title">
            <v-icon color="blue darken-3" class="mr-2">mdi-shield-account</v-icon>
            User Activity Logs
          </h1>
          <p class="grey--text text--darken-1 mb-0 mt-1 body-2">
            Full audit trail of all user actions · Logs auto-deleted after 30 days
          </p>
        </div>
        <v-spacer />
        <v-btn
          color="blue darken-3"
          dark
          small
          class="mr-2"
          :loading="loading"
          @click="loadLogs"
        >
          <v-icon left small>mdi-refresh</v-icon> Refresh
        </v-btn>
        <v-btn
          color="red darken-1"
          dark
          small
          outlined
          @click="confirmPurge = true"
        >
          <v-icon left small>mdi-delete-clock</v-icon> Purge Old (30d)
        </v-btn>
      </div>

      <!-- ───── Stats Bar ───── -->
      <v-row class="mb-4" dense>
        <v-col cols="12" sm="3" v-for="stat in statCards" :key="stat.label">
          <v-card class="stat-card pa-3" outlined>
            <div class="d-flex align-center">
              <v-avatar :color="stat.color" size="40" class="mr-3">
                <v-icon dark small>{{ stat.icon }}</v-icon>
              </v-avatar>
              <div>
                <div class="text-h6 font-weight-bold">{{ stat.value }}</div>
                <div class="caption grey--text">{{ stat.label }}</div>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- ───── Filter Panel ───── -->
      <v-card class="filter-card mb-4 pa-4" outlined>
        <p class="subtitle-2 mb-3 grey--text text--darken-2">
          <v-icon small class="mr-1">mdi-filter-variant</v-icon> Filter Logs
        </p>
        <v-row dense align="center">
          <!-- User filter -->
          <v-col cols="12" sm="3">
            <v-select
              v-model="filters.userId"
              :items="employeeOptions"
              item-text="label"
              item-value="id"
              label="User"
              outlined
              dense
              clearable
              hide-details
              prepend-inner-icon="mdi-account"
            />
          </v-col>

          <!-- Action type filter -->
          <v-col cols="12" sm="3">
            <v-select
              v-model="filters.action"
              :items="actionOptions"
              label="Action Type"
              outlined
              dense
              clearable
              hide-details
              prepend-inner-icon="mdi-lightning-bolt"
            />
          </v-col>

          <!-- Date from -->
          <v-col cols="12" sm="2">
            <v-text-field
              v-model="filters.dateFrom"
              label="From Date"
              type="date"
              outlined
              dense
              hide-details
              prepend-inner-icon="mdi-calendar-start"
            />
          </v-col>

          <!-- Date to -->
          <v-col cols="12" sm="2">
            <v-text-field
              v-model="filters.dateTo"
              label="To Date"
              type="date"
              outlined
              dense
              hide-details
              prepend-inner-icon="mdi-calendar-end"
            />
          </v-col>

          <!-- Buttons -->
          <v-col cols="12" sm="2" class="d-flex">
            <v-btn color="blue darken-3" dark @click="applyFilters" :loading="loading" class="mr-2 flex-grow-1">
              <v-icon left small>mdi-magnify</v-icon> Search
            </v-btn>
            <v-btn outlined @click="clearFilters" :disabled="loading">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <!-- Quick Action Type Chips -->
        <div class="mt-3 d-flex flex-wrap gap-2">
          <span class="caption grey--text mr-2 align-self-center">Quick filter:</span>
          <v-chip
            v-for="at in actionTypes"
            :key="at.value"
            small
            :color="filters.action === at.value ? at.color : 'grey lighten-3'"
            :dark="filters.action === at.value"
            @click="quickFilterAction(at.value)"
            class="mr-1 mb-1"
          >
            <v-icon left x-small>{{ at.icon }}</v-icon>
            {{ at.label }}
          </v-chip>
        </div>
      </v-card>

      <!-- ───── Summary ───── -->
      <div class="d-flex align-center mb-3">
        <span class="caption grey--text">
          Showing <strong>{{ logs.length }}</strong> of <strong>{{ total }}</strong> log entries
        </span>
        <v-spacer />
        <v-text-field
          v-model="tableSearch"
          placeholder="Search in results..."
          dense
          outlined
          hide-details
          prepend-inner-icon="mdi-magnify"
          style="max-width:260px"
          clearable
        />
      </div>

      <!-- ───── Data Table ───── -->
      <v-card outlined class="logs-table-card">
        <v-data-table
          :headers="headers"
          :items="logs"
          :search="tableSearch"
          :loading="loading"
          :items-per-page="50"
          :footer-props="{ itemsPerPageOptions: [25, 50, 100, -1] }"
          show-expand
          item-key="id"
          class="logs-table"
          loading-text="Loading activity logs..."
          no-data-text="No logs found matching your filters."
        >
          <!-- Timestamp -->
          <template v-slot:[`item.createdAt`]="{ item }">
            <span class="caption">{{ formatDate(item.createdAt) }}</span>
          </template>

          <!-- User -->
          <template v-slot:[`item.userName`]="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="28" :color="avatarColor(item.userName)" class="mr-2">
                <span class="white--text caption font-weight-bold">
                  {{ (item.userName || '?').charAt(0).toUpperCase() }}
                </span>
              </v-avatar>
              <div>
                <div class="body-2 font-weight-medium">{{ item.user ? item.user.name : item.userName }}</div>
                <div class="caption grey--text">@{{ item.userName }}</div>
              </div>
            </div>
          </template>

          <!-- Action chip -->
          <template v-slot:[`item.action`]="{ item }">
            <v-chip
              small
              :color="actionColor(item.action)"
              dark
              class="font-weight-medium"
            >
              <v-icon left x-small>{{ actionIcon(item.action) }}</v-icon>
              {{ actionLabel(item.action) }}
            </v-chip>
          </template>

          <!-- Description -->
          <template v-slot:[`item.description`]="{ item }">
            <span class="body-2">{{ formatDescription(item.description) }}</span>
          </template>

          <!-- Resource: invoice rows show a clickable open-invoice button -->
          <template v-slot:[`item.resourceType`]="{ item }">
            <span v-if="item.resourceType === 'Invoice' && item.resourceId"
                  class="d-flex align-center">
              <v-icon x-small class="mr-1">mdi-file-document</v-icon>
              <span class="caption mr-2">Invoice #{{ item.resourceId }}</span>
              <v-btn
                x-small
                depressed
                color="blue darken-3"
                dark
                @click="openInvoice(item.resourceId)"
              >
                <v-icon x-small left>mdi-open-in-new</v-icon> Open
              </v-btn>
            </span>
            <span v-else-if="item.resourceType" class="caption">
              <v-icon x-small class="mr-1">{{ resourceIcon(item.resourceType) }}</v-icon>
              {{ item.resourceType }}
            </span>
            <span v-else class="grey--text caption">—</span>
          </template>

          <!-- Expanded row: metadata JSON -->
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-0">
              <v-card flat class="metadata-panel ma-2 pa-3" color="grey lighten-5">
                <div class="d-flex align-center mb-2">
                  
                  <span class="caption font-weight-bold blue--text text--darken-3">Action Details</span>
                </div>
                <div v-if="item.metadata" class="metadata-content">
                  <div v-for="(val, key) in cleanMetadata(item.action, item.metadata)" :key="key" class="metadata-row">
                    <span class="metadata-key">{{ key }}</span>
                    <span class="metadata-sep">:</span>
                    <span class="metadata-val">{{ formatMetaValue(val) }}</span>
                  </div>
                </div>
                <span v-else class="caption grey--text">No additional metadata recorded.</span>
              </v-card>
            </td>
          </template>
        </v-data-table>
      </v-card>

      <!-- ───── Purge Confirmation Dialog ───── -->
      <v-dialog v-model="confirmPurge" max-width="420px" persistent>
        <v-card>
          <v-card-title class="red darken-2 white--text">
            <v-icon left dark>mdi-delete-clock</v-icon>
            Purge Old Logs
          </v-card-title>
          <v-card-text class="pt-4">
            This will permanently delete all activity logs older than <strong>30 days</strong>.
            This action <strong>cannot be undone</strong>.
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="confirmPurge = false">Cancel</v-btn>
            <v-btn color="red darken-2" dark :loading="purging" @click="executePurge">Delete Old Logs</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

    </template>
  </v-container>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { EventBus } from '../../eventbus/event-bus.js'

const ACTION_MAP = {
  LOGIN:          { label: 'Login',          color: 'green darken-1',  icon: 'mdi-login' },
  LOGOUT:         { label: 'Logout',         color: 'grey darken-1',   icon: 'mdi-logout' },
  INVOICE_CREATE: { label: 'Invoice Create', color: 'blue darken-2',   icon: 'mdi-file-plus' },
  INVOICE_UPDATE: { label: 'Invoice Update', color: 'orange darken-2', icon: 'mdi-file-edit' },
  INVOICE_POST:   { label: 'Invoice Post',   color: 'purple darken-1', icon: 'mdi-send' },
  PAYMENT_ADD:    { label: 'Payment Added',  color: 'teal darken-1',   icon: 'mdi-cash-plus' },
  INQUIRY_CREATE: { label: 'Inquiry Create', color: 'cyan darken-2',   icon: 'mdi-comment-plus' },
  INQUIRY_UPDATE: { label: 'Inquiry Update', color: 'amber darken-3',  icon: 'mdi-comment-edit' },
  INQUIRY_ACTION: { label: 'Inquiry Note',   color: 'light-blue darken-2', icon: 'mdi-comment-text' },
  USER_CREATE:    { label: 'User Created',   color: 'indigo darken-1', icon: 'mdi-account-plus' },
  USER_UPDATE:    { label: 'User Updated',   color: 'deep-orange darken-1', icon: 'mdi-account-edit' },
  USER_DELETE:    { label: 'User Deleted',   color: 'red darken-2',    icon: 'mdi-account-remove' },
}

const AVATAR_COLORS = ['blue darken-2','indigo darken-1','teal darken-1','purple darken-1','green darken-1','orange darken-2']

export default {
  name: 'ActivityLogsPage',

  data() {
    return {
      filters: {
        userId: null,
        action: null,
        dateFrom: null,
        dateTo: null,
      },
      tableSearch: '',
      confirmPurge: false,
      purging: false,

      headers: [
        { text: 'Timestamp',   value: 'createdAt',   width: '165px', sortable: true },
        { text: 'User',        value: 'userName',    width: '180px', sortable: true },
        { text: 'Action',      value: 'action',      width: '160px', sortable: true },
        { text: 'Description', value: 'description', sortable: false },
        { text: 'Resource',    value: 'resourceType', width: '140px', sortable: true },
        { text: '',            value: 'data-table-expand', width: '40px' },
      ],

      actionTypes: Object.entries(ACTION_MAP).map(([value, meta]) => ({
        value,
        label: meta.label,
        color: meta.color,
        icon: meta.icon
      })),
    }
  },

  computed: {
    ...mapState('activityLogs', ['logs', 'total', 'loading']),
    ...mapGetters('account', ['isAdmin']),

    employeeOptions() {
      const emps = this.$store.state.employees.employees || []
      return emps.map(e => ({ id: e.id, label: `${e.name} (@${e.userName})` }))
    },

    actionOptions() {
      return Object.entries(ACTION_MAP).map(([value, meta]) => ({
        value,
        text: meta.label,
      }))
    },

    statCards() {
      const total    = this.total
      const logins   = this.logs.filter(l => l.action === 'LOGIN').length
      const invCr    = this.logs.filter(l => l.action === 'INVOICE_CREATE').length
      const invUp    = this.logs.filter(l => l.action === 'INVOICE_UPDATE').length
      return [
        { label: 'Total Logs',       value: total,  color: 'blue darken-3',    icon: 'mdi-format-list-bulleted' },
        { label: 'Login Events',     value: logins, color: 'green darken-1',   icon: 'mdi-login' },
        { label: 'Invoices Created', value: invCr,  color: 'indigo darken-1',  icon: 'mdi-file-plus' },
        { label: 'Invoices Updated', value: invUp,  color: 'orange darken-2',  icon: 'mdi-file-edit' },
      ]
    }
  },

  mounted() {
    if (this.isAdmin) {
      this.loadLogs()
      this.$store.dispatch('employees/fetchEmployees')
    }
  },

  methods: {
    loadLogs() {
      this.$store.dispatch('activityLogs/fetchLogs', { limit: 200 })
    },

    applyFilters() {
      const params = { limit: 200 }
      if (this.filters.userId)   params.userId   = this.filters.userId
      if (this.filters.action)   params.action   = this.filters.action
      if (this.filters.dateFrom) params.dateFrom = this.filters.dateFrom
      if (this.filters.dateTo)   params.dateTo   = this.filters.dateTo

      const hasFilter = Object.keys(params).length > 1
      if (hasFilter) {
        this.$store.dispatch('activityLogs/filterLogs', params)
      } else {
        this.loadLogs()
      }
    },

    clearFilters() {
      this.filters = { userId: null, action: null, dateFrom: null, dateTo: null }
      this.tableSearch = ''
      this.loadLogs()
    },

    quickFilterAction(action) {
      if (this.filters.action === action) {
        this.filters.action = null
        this.applyFilters()
      } else {
        this.filters.action = action
        this.applyFilters()
      }
    },

    async executePurge() {
      this.purging = true
      try {
        await this.$store.dispatch('activityLogs/purgeLogs')
        this.confirmPurge = false
      } finally {
        this.purging = false
      }
    },

    /**
     * Navigate to /invoices and fire the EventBus event to load the specific invoice.
     * Same pattern used by NotificationPanel and FlightSearchReport.
     */
    openInvoice(invoiceId) {
      if (this.$route.path !== '/invoices') {
        this.$router.push('/invoices').then(() => {
          setTimeout(() => EventBus.$emit('loadInvoiceById', invoiceId), 300)
        })
      } else {
        EventBus.$emit('loadInvoiceById', invoiceId)
      }
    },

    // ── Formatters ──

    formatDescription(desc) {
      if (!desc) return '';
      // Strip legacy database ID patterns like "Inquiry #44" or "(ID: 5)"
      return desc.replace(/Inquiry #\d+\s?/, 'Inquiry ').replace(/\(ID: \d+\)/, '').trim();
    },

    formatDate(dt) {
      if (!dt) return '—'
      const d = new Date(dt)
      return d.toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false
      })
    },

    actionColor(action) {
      return (ACTION_MAP[action] || {}).color || 'grey'
    },

    actionIcon(action) {
      return (ACTION_MAP[action] || {}).icon || 'mdi-circle'
    },

    actionLabel(action) {
      return (ACTION_MAP[action] || {}).label || action
    },

    resourceIcon(type) {
      const m = { Invoice: 'mdi-file-document', User: 'mdi-account', Inquiry: 'mdi-comment-question' }
      return m[type] || 'mdi-cube-outline'
    },

    avatarColor(name) {
      if (!name) return AVATAR_COLORS[0]
      const idx = name.charCodeAt(0) % AVATAR_COLORS.length
      return AVATAR_COLORS[idx]
    },

    parseMetadata(raw) {
      if (!raw) return {}
      try { return JSON.parse(raw) } catch { return { raw } }
    },

    /**
     * Returns only the whitelisted keys for each action type.
     * This ensures old verbose DB records display cleanly.
     */
    cleanMetadata(action, raw) {
      if (!raw) return {}
      let parsed = {}
      try { parsed = JSON.parse(raw) } catch { return {} }

      // Whitelist of allowed keys per action group
      const ALLOWED = {
        INVOICE_CREATE: ['invoice', 'customer'],
        INVOICE_UPDATE: ['invoice', 'customer', 'changes'],
        INVOICE_POST:   ['postedCount', 'count'],
        LOGIN:          ['isAdmin'],
        LOGOUT:         ['role'],
        USER_CREATE:    ['newUser', 'newUserName', 'isAdmin'],
        USER_UPDATE:    ['targetUser', 'targetUserName', 'isAdmin', 'passwordChanged', 'changes'],
        USER_DELETE:    ['deletedUserName', 'deletedUserId'],
        INQUIRY_CREATE: ['title', 'category', 'priority'],
        INQUIRY_UPDATE: ['title', 'status', 'newStatus', 'changes'],
        INQUIRY_ACTION: ['actionText'],
      }

      const allowed = ALLOWED[action]
      if (!allowed) return parsed   // unknown action — show as-is

      // Only return entries whose key is in the whitelist
      return Object.fromEntries(
        Object.entries(parsed).filter(([k]) => allowed.includes(k))
      )
    },

    formatMetaValue(val) {
      if (val === null || val === undefined) return '—'
      if (typeof val === 'object') return JSON.stringify(val)
      return String(val)
    },
  }
}
</script>

<style scoped>
.activity-logs-page {
  max-width: 1400px;
  margin: 0 auto;
}

.logs-title {
  color: #1565C0;
}

.stat-card {
  border-radius: 10px !important;
  transition: box-shadow 0.2s;
}
.stat-card:hover {
  box-shadow: 0 4px 16px rgba(21, 101, 192, 0.12) !important;
}

.filter-card {
  border-radius: 10px !important;
  border-color: #e0e0e0 !important;
}

.logs-table-card {
  border-radius: 10px !important;
  overflow: hidden;
}

.logs-table >>> thead th {
  background-color: #1565C0 !important;
  color: white !important;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
}

.logs-table >>> tbody tr:hover {
  background-color: #e3f2fd !important;
}

.logs-table >>> tbody tr:nth-child(even) {
  background-color: #f5f9ff;
}

.metadata-panel {
  border-radius: 8px !important;
  border-left: 4px solid #1565C0 !important;
}

.metadata-content {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2px;
}

.metadata-row {
  display: flex;
  align-items: flex-start;
  padding: 2px 0;
  font-size: 12px;
}

.metadata-key {
  font-weight: 600;
  color: #1565C0;
  min-width: 160px;
  flex-shrink: 0;
}

.metadata-sep {
  margin: 0 6px;
  color: #9e9e9e;
}

.metadata-val {
  color: #212121;
  word-break: break-all;
}

.font-mono {
  font-family: 'Roboto Mono', monospace;
}

.gap-2 {
  gap: 8px;
}
</style>